<template>
  <div class="michelson-editor">
    <textarea ref="michelsonEditor"></textarea>
  </div>
</template>

<script>
import { emitMicheline } from '@taquito/michel-codec'
import CodeMirror from 'codemirror'

require('codemirror/addon/mode/simple.js')

// Borrowed from better-call.dev!
CodeMirror.defineSimpleMode("michelson", {
  start: [
    // delimiters
    { regex: /[;{(\s]/, token: "builtin", next: "start" },
    // string
    { regex: /"(?:[^\\]|\\.)*?(?:"|$)/, token: "string" },
    // bytes
    { regex: /(?:0x[0-9a-f]+)(?=\s|;|\}|\)|$)/i, token: "string" },
    // int
    { regex: /(?:[+-]?[0-9]+\.?[0-9]*)(?=\s|;|\}|\)|$)/, token: "string" },
    // comment
    { regex: /#.*/, token: "comment" },
    { regex: /\/\*/, token: "comment", next: "comment" },
    // block
    { regex: /(?:parameter|storage|code)(?=\s|$)/, token: "atom" },
    // data
    { regex: /(?:Unit|True|False|Pair|Left|Right|Some|None|Elt)(?=\s|;|\)|$)/, token: "keyword" },
    // instruction
    { regex: /(?:CAST|RENAME|DROP|DUP|SWAP|PUSH|SOME|NONE|UNIT|NEVER|IF_NONE|UNPAIR|PAIR|CAR|CDR|LEFT|RIGHT|IF_LEFT|IF_RIGHT|NIL|CONS|IF_CONS|SIZE|EMPTY_SET|EMPTY_MAP|MAP|ITER|MEM|GET|UPDATE|IF|LOOP|LOOP_LEFT|LAMBDA|EXEC|DIP|FAILWITH|CONCAT|SLICE|PACK|UNPACK|ADD|SUB|MUL|EDIV|ABS|NEG|LSL|LSR|OR|AND|XOR|NOT|COMPARE|EQ|NEQ|LT|GT|LE|GE|CHECK_SIGNATURE|BLAKE2B|SHA256|SHA512|SHA3|KECCAK|HASH_KEY|DIG|DUG|EMPTY_BIG_MAP|APPLY|ISNAT|INT|PAIRING_CHECK)(?=\s|;|\}|$)/, token: "keyword" },
    { regex: /(?:SELF_ADDRESS|SELF|CONTRACT|TRANSFER_TOKENS|SET_DELEGATE|CREATE_CONTRACT|IMPLICIT_ACCOUNT|NOW|AMOUNT|BALANCE|STEPS_TO_QUOTA|SOURCE|SENDER|ADDRESS|CHAIN_ID|SAPLING_VERIFY_UPDATE|SAPLING_EMPTY_STATE|TOTAL_VOTING_POWER|VOTING_POWER|LEVEL)(?=\s|;|\}|$)/, token: "atom" },
    // type
    { regex: /(?:option|list|set|contract|pair|or|lambda|map|big_map)(?=\s|\)|$)/, token: "tag" },
    { regex: /(?:key|unit|never|signature|operation|address|int|nat|string|bytes|mutez|bool|key_hash|baker_hash|timestamp|chain_id|sapling_state|sapling_transaction|bls12_381_fr|bls12_381_g1|bls12_381_g2)(?=\s|\)|\}|;|$)/, token: "variable" },
    // macros
    { regex: /(?:IF_SOME|FAIL|ASSERT|ASSERT_NONE|ASSERT_SOME|ASSERT_LEFT|ASSERT_RIGHT|(?:SET|MAP)_C[AD]+R)(?=\s|;|\}|$)/, token: "string-2" },
    { regex: /(?:DII+P|C[AD]{2,}R|DUU+P|P[PAI]{3,}R|UNP[PAI]{3,}R)(?=\s|;|\}|$)/, token: "string-2" },
    { regex: /(?:(?:CMP|IF|IFCMP|ASSERT_|ASSERT_CMP)(?:EQ|NEQ|LT|GT|LE|GE))(?=\s|;|\}|\{|$)/, token: "string-2" },
    // annotations
    { regex: /(?:%[A-z_0-9%@]*)(?=\s|$|\))/, token: "comment" },
    { regex: /(?:@[A-z_0-9%]+)(?=\s|$|\))/, token: "comment" },
    { regex: /(?::[A-z_0-9]+)(?=\s|$|\))/, token: "comment" },
    // fallback
    { regex: /[^\s]+/, token: "builtin" }
  ],
  comment: [
    { regex: /.*?\*\//, token: "comment", next: "start" },
    { regex: /.*/, token: "comment" }
  ],
  meta: {
    dontIndentStates: ["comment"],
    lineComment: "#",
    blockCommentStart: "/*",
    blockCommentEnd: "*/"
  }
});

export default {
  name: 'MichelsonEditor',
  props: {
    readOnly: {
      default: true
    },
    michelsonCode: {
      default: null
    },
    michelsonRaw: {
      default: null
    },
  },
  data(){
    return {
      editor: null
    }
  },
  components: {},
  async mounted(){
    this.editor = CodeMirror.fromTextArea(this.$refs.michelsonEditor, {
      mode: "michelson",
      readOnly: this.readOnly,
      lineNumbers: true
    });

    this.editor.on('change', (cm) => {
      this.$emit('editor-change', cm.getValue())
    })

    if (this.michelsonCode !== null && this.michelsonRaw === null){
      // this.editor.setValue(emitMicheline(this.michelsonCode, {indent:"  ", newline: "\n",}))
      this.editor.setValue(
        // Format using taquito and clean up the weird formatting they use
        emitMicheline(this.michelsonCode, {indent:"    ", newline: "\n",})
          .replaceAll(/{\n\s+/g, '{ ')
          .replaceAll(/\n^\s+\(/gm, ' (')
          .replaceAll(/\n^\s+([a-z])/gm, ' $1')
          .replaceAll(
            /^(\s+IF_NONE)\n\s+({[^\n]+)\n\s+(FAILWITH)\n\s+(})\s*\n\s+({\s};)/gm,
            '$1 $2 $3 $4 $5'
          )
      )
    } else if (this.michelsonRaw !== null) {
      this.editor.setValue(this.michelsonRaw)
    } else {
      this.editor.setValue(`{
    DROP;
    NIL operation;
}`)
    }
  },
  watch: {
    michelsonRaw(newData){
      this.editor.setValue(newData)
    }
  }
}
</script>

<style lang="scss">
@import '../assets/sass/globals';
@import '~codemirror/lib/codemirror.css';
.michelson-editor{
  .CodeMirror{
    border: 2px solid #f5f5f5;
    border-radius: 5px;
    margin: 1rem 0;
    height: auto;
    .CodeMirror-line{
      margin-bottom: 0 !important;
    }
  }

}

</style>
